import "./LabDetailsScreen.scss";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import {CommonService} from "../../../shared/services";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import LabInformationAndContactDetailsComponent
    from "./lab-information-and-contact-details/LabInformationAndContactDetailsComponent";
import LabDirectorDetailsComponent from "./lab-director-details/LabDirectorDetailsComponent";
import {IAPIResponseType} from "../../../shared/models/api.model";
import LabAttachmentsComponent from "./lab-attachments/LabAttachmentsComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import LabAdminDetailsViewEditComponent from "./lab-admin-details-view-edit/LabAdminDetailsViewEditComponent";
import {ImageConfig} from "../../../constants";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import LabReportDetailsViewEditComponent from "./lab-report-details-view-edit/LabReportDetailsViewEditComponent";
import LabTestAndPanelViewEditComponent from "./lab-test-and-panel-view-edit/LabTestAndPanelViewEditComponent";
import LabUserDetailsViewEditComponent from "./lab-user-details-view-edit/LabUserDetailsViewEditComponent";
import {LabRoleType} from "../../../shared/models/lab.model";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import LabRoleSelectionComponent from "../../../shared/components/labRoleSelection/LabRoleSelectionComponent";
import {GENERIC_LAB_ADD_STEPS} from "../lab-add/LabAddComponent";

interface LabDetailsComponentProps {

}

const ADMIN_DETAILS_TAB = 'admin_details';
const LAB_DETAILS_TAB = 'lab_details';
const LAB_ATTACHMENTS_TAB = 'lab_attachments';
const DIRECTOR_DETAILS_TAB = 'director_details';
const TEST_AND_PANELS_TAB = 'test_and_panels';
const REPORT_SETUP_TAB = 'report_setup';
const USER_DETAILS = 'user_details';

const sendLabTabs = [LAB_DETAILS_TAB, ADMIN_DETAILS_TAB, LAB_ATTACHMENTS_TAB];
const sendReceiveLabTabs = [LAB_DETAILS_TAB, ADMIN_DETAILS_TAB, DIRECTOR_DETAILS_TAB, TEST_AND_PANELS_TAB, LAB_ATTACHMENTS_TAB, REPORT_SETUP_TAB];
const inHouseLabTabs = [LAB_DETAILS_TAB, ADMIN_DETAILS_TAB, USER_DETAILS, DIRECTOR_DETAILS_TAB, TEST_AND_PANELS_TAB, LAB_ATTACHMENTS_TAB, REPORT_SETUP_TAB];


const LabDetailsScreen = (props: LabDetailsComponentProps) => {
    const [activeTab, setActiveTab] = useState<"admin_details" | "lab_details" | "lab_attachments" | "director_details" | "report_setup" | "test_and_panels" | "user_details">(ADMIN_DETAILS_TAB);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const params = useParams<{ labID: string | undefined }>();
    const {labRole} = useParams<{ labRole?: LabRoleType }>();
    const [labDetails, setLabDetails] = useState<any>(undefined);
    // Determine which tabs to show based on the labRole
    const tabsToShow = useMemo(() => labDetails?.labType === 'inHouseLab' ? inHouseLabTabs : (labRole === 'sendLab' ? sendLabTabs : sendReceiveLabTabs), [labRole, labDetails]);
    const {labID} = params;
    const [isLabDetailsLoading, setIsLabDetailsLoading] = useState<boolean>(false);
    const [isLabDetailsLoaded, setIsLabDetailsLoaded] = useState<boolean>(false);
    const [isLabDetailsLoadingFailed, setIsLabDetailsLoadingFailed] = useState<boolean>(false);
    const [labDirectorDetails, setLabDirectorDetails] = useState<any>(undefined);
    const dispatch = useDispatch();
    const [labDetailsMode, setLabDetailsMode] = useState<"view" | "edit">("view");
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const [isLabSelectionDrawerOpen, setIsLabSelectionDrawerOpen] = useState(false);
    const [isLabSwitchLoading, setIsLabSwitchLoading] = useState(false);

    useEffect(() => {
        setCanEdit(commonService.checkAccess('Lab',
            "labEdit", currentUser?.userAccessModules))
        setActiveTab(searchParams.get("currentTab") as typeof activeTab || ADMIN_DETAILS_TAB)
    }, [currentUser, searchParams]);

    useEffect(() => {
        if (labID) {
            dispatch(setCurrentNavParams("Back", "Lab", CommonService._routeConfig.LabListRoute()));
            dispatch(setSideMenuView('compact'));
        }
    }, [labID, dispatch, navigate]);

    const getLabDetails = useCallback(() => {
        const payload = {};
        let labDetails: any = {};
        setIsLabDetailsLoading(true);
        setIsLabDetailsLoaded(false);
        setIsLabDetailsLoadingFailed(false);
        CommonService._lab.getLabDetails(labID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    labDetails = response.data;
                }
                setLabDetails(labDetails);
                setLabDirectorDetails(labDetails?.directorDetails);
                setIsLabDetailsLoading(false);
                setIsLabDetailsLoaded(true);
                setIsLabDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setLabDetails(labDetails);
                setIsLabDetailsLoading(false);
                setIsLabDetailsLoaded(false);
                setIsLabDetailsLoadingFailed(true);
            });
    }, [labID]);

    useEffect(() => {
        if (labID) {
            getLabDetails();
        }
    }, [labID, getLabDetails])

    const handleLabDetailsUpdate = useCallback((labBasicDetails: any) => {
        getLabDetails();
        setLabDetails((oldState: any) => {
            return {
                ...oldState,
                ...labBasicDetails
            }
        });
    }, [getLabDetails]);

    const handleAdminDetailsUpdate = useCallback((labAdminDetails: any) => {
        setLabDetails(() => {
            return {
                ...labDetails,
                ...labAdminDetails,
            }
        });
    }, [labDetails]);

    // const handleLabDirectorDetailsUpdate = useCallback((updatedLabDirectorDetails: any) => {
    //     setLabDirectorDetails(() => {
    //         return {
    //             ...updatedLabDirectorDetails
    //         }
    //     });
    // }, []);

    const handleLabReportDetailsUpdate = useCallback((logo: any, themeColor: any) => {
        setLabDetails(() => {
            return {
                ...labDetails,
                logo: logo,
                themeColor: themeColor
            }
        });
    }, [labDetails]);

    const handleSwitchLabRole = useCallback((currentLabRole: LabRoleType) => {
        const payload = {
            labId: labID,
            isCompleted: false,
            labRole: currentLabRole
        }
        setIsLabSwitchLoading(true)
        const id = CommonService._alert.loadingToast("Switching lab role");
        CommonService._lab.updateLabRoleStatus(payload).then((response) => {
            // Destructure the necessary values from response.data
            const {labRole: updatedLabRole, lastCompletedStep = 1} = response?.data || {}; // default to empty object if response.data is undefined

            // Ensure lastCompletedStep has a valid value
            const stepId = GENERIC_LAB_ADD_STEPS[lastCompletedStep]?.id;
            if (stepId === undefined) {
                return CommonService._alert.updateToast(id, "Invalid step ID", "error");
            }

            // Construct the URL with the extracted data
            const route = CommonService._routeConfig.LabCreateRoute(updatedLabRole);
            const url = `${route}?labId=${labID}&step=${stepId}`;

            CommonService._alert.updateToast(id, response.message || 'Updated lab role', 'success');
            navigate(url);
        }).catch((error): any => {
            CommonService._alert.updateToast(id, error.error || 'Failed to switch Lab', 'error');
        }).finally(() => {
            setIsLabSwitchLoading(false)
        })
    }, [labID, navigate])

    const handleActiveTabChange = useCallback((value: any) => {
        if (labDetailsMode === 'edit') {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Discard",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                setActiveTab(value);
            }).catch(() => {

            })
        } else {
            setActiveTab(value);
        }
        searchParams.set("currentTab", value);
        setSearchParams(searchParams);
    }, [labDetailsMode, searchParams, setSearchParams])

    return (
        <div className="lab-details-screen details-screen screen">
            {
                isLabDetailsLoading && <div className="h-v-center">
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
            {
                isLabDetailsLoadingFailed && <>
                    <StatusCardComponent title={"Lab details failed to load"}/>
                </>
            }
            {
                isLabDetailsLoaded && <>
                    <div className={'details-module-title'}>Lab Details</div>
                    <div className="basic-details-wrapper">
                        <div className="details-screen-header">
                            <div className="d-flex justify-content-space-between">
                                <HeadingComponent title={CommonService.capitalizeFirstLetter(labDetails?.name)}
                                />
                                {canEdit && <ButtonComponent
                                    variant={'outlined'}
                                    color={'secondary'}
                                    startIcon={<PublishedWithChangesIcon/>}
                                    disabled={labDetails?.labType === 'inHouseLab'}
                                    handleClick={() => setIsLabSelectionDrawerOpen(true)}
                                >
                                    Change Functionality
                                </ButtonComponent>}
                            </div>
                        </div>
                        <div className="details-screen-sub-header">
                            <DetailsDataKeyValueComponent
                                title={"Lab ID:"}
                                isHorizontal={true}>
                                <>{labDetails?.labExternalId}</>
                            </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Lab Type:"}
                                isHorizontal={true}>
                                <>{CommonService.mapLabTypeToName(labDetails.labType)}</>
                            </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent title={"Lab Role:"} isHorizontal>
                                {CommonService.mapLabRoleToName(labDetails?.labRole)}
                            </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Created on:"}
                                isHorizontal={true}>
                                <>
                                    {commonService.formatDate(labDetails?.createdAt)}
                                    &nbsp;|&nbsp;
                                    {commonService.formatTime(labDetails?.createdAt)}
                                </>
                            </DetailsDataKeyValueComponent>
                        </div>
                    </div>
                    <div className="clear-fix"/>
                    <div className="details-screen-body">
                        <TabsWrapperComponent>
                            <TabsComponent
                                value={activeTab}
                                allowScrollButtonsMobile={false}
                                onUpdate={(e: any, v: any) => {
                                    handleActiveTabChange(v)
                                }}
                                variant={"fullWidth"}
                            >
                                {tabsToShow.includes(ADMIN_DETAILS_TAB) &&
                                    <TabComponent label="Admin Details" value={ADMIN_DETAILS_TAB}/>}
                                {tabsToShow.includes(USER_DETAILS) &&
                                    <TabComponent label={"User Details"} value={USER_DETAILS}/>}
                                {tabsToShow.includes(LAB_DETAILS_TAB) &&
                                    <TabComponent label="Lab Details" value={LAB_DETAILS_TAB}/>}
                                {tabsToShow.includes(LAB_ATTACHMENTS_TAB) &&
                                    <TabComponent label="Lab Attachments" value={LAB_ATTACHMENTS_TAB}/>}
                                {tabsToShow.includes(DIRECTOR_DETAILS_TAB) &&
                                    <TabComponent label="Director Details" value={DIRECTOR_DETAILS_TAB}/>}
                                {tabsToShow.includes(TEST_AND_PANELS_TAB) &&
                                    <TabComponent label="Test and Panels" value={TEST_AND_PANELS_TAB}/>}
                                {tabsToShow.includes(REPORT_SETUP_TAB) &&
                                    <TabComponent label="Report Setup" value={REPORT_SETUP_TAB}/>}
                            </TabsComponent>

                            <TabContentComponent selectedTab={activeTab} value={ADMIN_DETAILS_TAB}>
                                <LabAdminDetailsViewEditComponent
                                    onUpdateAdminDetails={handleAdminDetailsUpdate}
                                    labDetails={labDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={USER_DETAILS}>
                                <LabUserDetailsViewEditComponent
                                    onUpdateAdminDetails={setLabDetails}
                                    labDetails={labDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={LAB_DETAILS_TAB}>
                                <LabInformationAndContactDetailsComponent
                                    onUpdateLabInformation={handleLabDetailsUpdate}
                                    labDetails={labDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={LAB_ATTACHMENTS_TAB}>
                                <LabAttachmentsComponent
                                    labDetails={labDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={DIRECTOR_DETAILS_TAB}>
                                <LabDirectorDetailsComponent
                                    directorDetails={labDirectorDetails}
                                    labDetails={labDetails}
                                    labID={labID}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }}
                                    handleLabDetailsUpdate={handleLabDetailsUpdate}
                                />
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={TEST_AND_PANELS_TAB}>
                                <LabTestAndPanelViewEditComponent
                                    labID={labID}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>

                            <TabContentComponent selectedTab={activeTab} value={REPORT_SETUP_TAB}>
                                <LabReportDetailsViewEditComponent
                                    labDetails={labDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLabDetailsMode(mode);
                                    }
                                    }
                                    onUpdateLabReportDetails={handleLabReportDetailsUpdate}
                                />
                            </TabContentComponent>
                        </TabsWrapperComponent>
                    </div>
                </>
            }
            <LabRoleSelectionComponent onLabSelection={handleSwitchLabRole} currentLabRole={labRole}
                                       isOpen={isLabSelectionDrawerOpen}
                                       isLoading={isLabSwitchLoading}
                                       onClose={() => setIsLabSelectionDrawerOpen(false)}/>
        </div>
    );

};

export default LabDetailsScreen;
